/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { GetRequest, PatchJSON, PostRequest } from "@/lib/http";
import { cableName, meterType } from "@/constants";
import { Loader2, XIcon } from "lucide-react";
import { keycard, phone, userImg } from "@/assets";
import { AlertDialog, AlertDialogContent } from "../ui/alert-dialog";
import { toast } from "sonner";

const AddBeneficiaryModal = ({ open, onClose, setRefetch, editItem }) => {
  const [loading, setLoading] = useState(false);
  const [telcos, setTelcos] = useState([]);
  const [electricity, setElectricity] = useState([]);
  const [error, setError] = useState("");
  const [electricityFetch, setElectricityFetch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const formType = editItem?.type || localStorage.getItem("beneficiaryType");

  async function fetchElectricity() {
    const { err, data: apiData } = await GetRequest({
      url: "/api/electricity-details",
      setState: setElectricityFetch,
    });

    if (!err) {
      setElectricity(apiData.discos.slice(1));
    } else {
      setError(err);
    }
  }
  async function fetchAirtime() {
    const { data, err } = await GetRequest({
      url: "/api/airtime-details",
      setState: setLoading,
    });

    // console.log(data);

    if (!err) {
      setTelcos(data.networks.slice(1));
    } else {
      toast.error(err);
    }
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "",
      network: "",
      value: "",
      name: "",
      meter_type: "",
      cable: "",
      disco: "",
    },
  });

  const closeModal = () => {
    onClose();
    reset({
      type: "",
      network: "",
      value: "",
      name: "",
      meter_type: "",
      cable: "",
      disco: "",
    });
    setRefetch();
    localStorage.removeItem("beneficiaryType");
  };

  // Determine providers based on form type
  let providers = [];
  if (formType === "phone") {
    providers = ["MTN", "GLO", "AIRTEL", "9MOBILE"];
  } else if (formType === "meter") {
    providers = electricity;
  } else {
    providers = cableName;
  }

  const onSubmit = async (data) => {
    if (editItem) {
      const { err } = await PatchJSON({
        url: `/api/beneficiary/${editItem.id}`,
        setState: setIsEdit,
        body: { ...data, type: formType },
      });

      if (!err) {
        toast.success("Successful", {
          description: "Beneficiary edited successfully",
        });
        closeModal();
      } else {
        toast.error("Error while updating beneficiary", {
          description: err,
        });
      }
    } else {
      const { err } = await PostRequest({
        url: "/api/beneficiary",
        body: { ...data, type: formType },
        setState: setIsSubmitting,
      });

      if (!err) {
        toast.success("Success", {
          description: "Beneficiary added successfully",
        });
        closeModal();
      } else {
        toast.error("Error while adding beneficiary", {
          description: err,
        });
      }
    }
  };

  useEffect(() => {
    if (editItem) {
      setValue("type", editItem.type);
      setValue("network", editItem.network);
      setValue("value", editItem.value);
      setValue("name", editItem.name);
      setValue("meter_type", editItem.meter_type);
      setValue("cable", editItem.cable);
      setValue("disco", editItem.disco);
    }
  }, [editItem]);

  const renderFormContent = () => {
    if (formType === "phone") {
      return (
        <>
          <div className="flex items-start justify-between">
            <img src={phone} alt="phone-icon" className="w-12" />

            <XIcon className="cursor-pointer" onClick={closeModal} />
          </div>
          <h2 className="text-xl font-semibold">What&apos;s your number?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add your default number so you can quickly buy airtime or data in
            the future.
          </p>
        </>
      );
    } else if (formType === "meter") {
      return (
        <>
          <div className="flex items-start justify-between">
            <img src={userImg} alt="meter-icon" className="w-12 dark:invert" />

            <XIcon className="cursor-pointer" onClick={closeModal} />
          </div>

          <h2 className="text-xl font-semibold">Enter your Meter Number</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add your meter number so you can quickly top up in the future.
          </p>
        </>
      );
    } else if (formType === "iuc") {
      return (
        <>
          <div className="flex items-start justify-between">
            <div className="flex size-14 items-center justify-center rounded-full bg-black">
              <img src={keycard} alt="smartcard-icon" className="w-[2.7rem]" />
            </div>

            <XIcon className="cursor-pointer" onClick={onClose} />
          </div>

          <h2 className="text-xl leading-[1.1] font-semibold">
            Enter your Smartcard Number
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add your smartcard number so you can quickly pay for your utilities.
          </p>
        </>
      );
    }
  };

  useEffect(() => {
    setValue("type", formType);
  }, [formType, setValue]);

  useEffect(() => {
    fetchAirtime();
    fetchElectricity();
  }, []);

  return (
    <AlertDialog open={open} onOpenChange={closeModal}>
      <AlertDialogContent className="dark:border-darkBg">
        {renderFormContent()}

        {loading || electricityFetch ? (
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            {/* Provider Selection */}
            <div className="flex flex-col gap-2">
              <label htmlFor="provider" className="text-sm font-medium">
                Provider
              </label>
              <Select
                value={watch(
                  formType === "phone"
                    ? "network"
                    : formType === "meter"
                      ? "disco"
                      : formType === "iuc"
                        ? "cable"
                        : "",
                )}
                onValueChange={(value) =>
                  setValue(
                    formType === "phone"
                      ? "network"
                      : formType === "meter"
                        ? "disco"
                        : formType === "iuc"
                          ? "cable"
                          : "",
                    value,
                    { shouldValidate: true },
                  )
                }
                {...register(
                  formType === "phone"
                    ? "network"
                    : formType === "meter"
                      ? "disco"
                      : formType === "iuc"
                        ? "cable"
                        : "",
                  { required: "Provider is required" },
                )}
                className="focus:border-slate-40 w-full rounded-md border border-slate-400 px-3"
              >
                <SelectTrigger className="py-5">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="lg:-top-16">
                  <SelectGroup>
                    {providers.map((item, index) => (
                      <SelectItem
                        key={index}
                        value={
                          formType === "phone"
                            ? item
                            : formType === "meter"
                              ? item.split(" ")[0]
                              : item.name
                        }
                      >
                        {formType === "phone" || formType === "meter"
                          ? item
                          : item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>{" "}
              {errors.provider && (
                <p className="text-xs font-medium text-red-500">
                  {errors.provider.message}
                </p>
              )}
            </div>

            {/* Phone Number Input for Data Airtime */}
            {formType === "phone" && (
              <div className="flex flex-col gap-2">
                <label htmlFor="value" className="text-sm font-medium">
                  Phone Number
                </label>
                <input
                  type="tel"
                  {...register("value", {
                    required: "Phone Number is required",
                    pattern: {
                      value: /^[0-9]{11,12}$/,
                      message:
                        "Phone number must be 11 or 12 digits e.g 09123456789",
                    },
                    onChange: (e) => {
                      let value = e.target.value;
                      if (value.startsWith("+234")) {
                        value = "0" + value.slice(4);
                        e.target.value = value.replace(/[^0-9]/g, "");
                      }
                    },
                  })}
                  className="inputClass"
                  placeholder="Enter phone number"
                />
                {errors.value && (
                  <p className="text-xs font-medium text-red-500">
                    {errors.value.message}
                  </p>
                )}
              </div>
            )}

            {/* Electricity and Cable Inputs */}
            {formType === "meter" && (
              <>
                <div className="flex flex-col gap-2">
                  <label htmlFor="meter_type" className="text-sm font-medium">
                    Bill Type
                  </label>
                  <Select
                    value={watch("meter_type")}
                    onValueChange={(value) =>
                      setValue("meter_type", value, { shouldValidate: true })
                    }
                    {...register("meter_type", {
                      required: "Please select meter type",
                    })}
                    className="focus:border-slate-40 w-full rounded-md border border-slate-400 px-3"
                  >
                    <SelectTrigger className="py-5">
                      <SelectValue placeholder="Select meter type" />
                    </SelectTrigger>
                    <SelectContent className="lg:-top-16">
                      <SelectGroup>
                        {meterType.map((item) => (
                          <SelectItem key={item.id} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {errors.meter_type && (
                    <p className="text-xs font-medium text-red-500">
                      {errors.meter_type.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="number" className="text-sm font-medium">
                    Meter Number
                  </label>
                  <input
                    type="text"
                    autoComplete="off"
                    inputMode="numeric"
                    {...register("value", {
                      required: "Meter number is required",
                      pattern: {
                        value: /^[0-9]/,
                        message: "Meter Number must be a number",
                      },
                    })}
                    className="inputClass"
                    placeholder="Enter meter number"
                  />
                  {errors.value && (
                    <p className="text-xs font-medium text-red-500">
                      {errors.value.message}
                    </p>
                  )}
                </div>
              </>
            )}

            {formType === "iuc" && (
              <div className="flex flex-col gap-2">
                <label htmlFor="value" className="text-sm font-medium">
                  SmartCard Number
                </label>

                <input
                  type="text"
                  autoComplete="off"
                  inputMode="numeric"
                  {...register("value", {
                    required: "Smartcard number is required",
                    pattern: {
                      value: /^[0-9]/,
                      message: "value must be a number",
                    },
                  })}
                  className="inputClass"
                  placeholder="e.g 7266378FGY"
                />
                {errors.value && (
                  <p className="text-xs font-medium text-red-500">
                    {errors.value.message}
                  </p>
                )}
              </div>
            )}

            {/* Nickname Input */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                Nickname
              </label>
              <input
                type="text"
                {...register("name", {
                  required: "Please enter nickname",
                })}
                className="inputClass"
                placeholder="e.g. Mom, Sister, John from work"
              />
              {errors.name && (
                <p className="text-xs font-medium text-red-500">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="bg-primary flex w-full cursor-pointer items-center justify-center gap-2 rounded-md py-2 text-white disabled:opacity-60"
              disabled={isSubmitting || isEdit}
            >
              {isSubmitting || isEdit ? (
                <Loader2 className="animate-spin" />
              ) : (
                "Save Beneficiary"
              )}
            </button>
          </form>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AddBeneficiaryModal;
